import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { X, CaretLeft, CaretRight, Eraser, DeviceMobile, MapPin } from "@phosphor-icons/react";
import { STATUS_STYLE } from "@/components/hr/attendanceStatus";

const STATUSES = [["P", "Present"], ["OD", "On Duty"], ["WO", "Weekly Off"], ["H", "Holiday"], ["HD", "Half Day"], ["L", "Leave"], ["A", "Absent"]];
const toMin = (t) => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };

export default function AttendanceCellEditor({ rect, employee, day, settings, base, onSaved, onClose, onMove }) {
  const [form, setForm] = useState({ status: day.status || "", in_time: day.in_time || "", out_time: day.out_time || "", remarks: day.remarks || "" });
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  const saveSoon = (patch) => {
    const next = { ...formRef.current, ...patch };
    setForm(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save({}, next), 500);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.altKey && e.key === "ArrowRight") onMove(1); if (e.altKey && e.key === "ArrowLeft") onMove(-1); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [onClose, onMove]);

  const save = async (patch, snapshot) => {
    clearTimeout(timer.current);
    const next = { ...(snapshot || formRef.current), ...patch };
    setForm(next);
    setSaving(true);
    try {
      const { data } = await api.put(`${base}/${employee.id}/day/${day.day}`, next);
      onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const lateMin = form.in_time && (form.status === "" || form.status === "P" || form.status === "OD") ? Math.max(0, toMin(form.in_time) - toMin(settings.shift_in)) : 0;
  const isLate = lateMin > (settings.late_grace_min ?? 30);
  const earlyMin = form.out_time && (form.status === "" || form.status === "P" || form.status === "OD") ? Math.max(0, toMin(settings.shift_out) - toMin(form.out_time)) : 0;
  const isEarly = settings.early_exit_deduction && earlyMin > (settings.early_grace_min ?? 30);
  const hours = form.in_time && form.out_time && toMin(form.out_time) > toMin(form.in_time) ? ((toMin(form.out_time) - toMin(form.in_time)) / 60).toFixed(1) : null;

  const W = 300;
  const left = Math.min(Math.max(8, rect.left + rect.width / 2 - W / 2), window.innerWidth - W - 8);
  const below = rect.bottom + 8 + 300 < window.innerHeight;
  const style = { position: "fixed", left, width: W, zIndex: 60, ...(below ? { top: rect.bottom + 8 } : { bottom: window.innerHeight - rect.top + 8 }) };

  return (
    <div ref={ref} style={style} className="bg-white border border-slate-200 shadow-xl rounded-sm p-3 text-[11.5px] animate-in fade-in zoom-in-95 duration-100" data-testid="attendance-cell-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-semibold text-slate-900 leading-tight">{employee.name}</div>
          <div className="text-[10.5px] text-slate-500 font-mono-num">{day.date} · {day.weekday}{day.auto_status ? ` · default ${day.auto_status}` : ""}</div>
        </div>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onMove(-1)} title="Previous day (Alt+←)" data-testid="attendance-editor-prev"><CaretLeft size={12} /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onMove(1)} title="Next day (Alt+→)" data-testid="attendance-editor-next"><CaretRight size={12} /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose} data-testid="attendance-editor-close"><X size={12} /></Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1 mb-2">
        {STATUSES.map(([code, label]) => (
          <button key={code} onClick={() => save({ status: code })} title={label} disabled={saving}
                  className={`h-8 rounded-sm border text-[10.5px] font-bold transition-colors ${STATUS_STYLE[code]} ${form.status === code ? "ring-2 ring-[#EA580C] ring-offset-1" : "opacity-80 hover:opacity-100"}`}
                  data-testid={`attendance-editor-status-${code}`}>
            {code}<div className="text-[8px] font-normal">{label}</div>
          </button>
        ))}
        <button onClick={() => save({ status: "", in_time: "", out_time: "", remarks: "" })} disabled={saving} title="Clear this day"
                className="h-8 rounded-sm border border-dashed border-slate-300 text-slate-500 text-[10px] hover:bg-slate-50 flex items-center justify-center gap-1" data-testid="attendance-editor-clear">
          <Eraser size={11} /> Clear
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-500">In time</div>
          <input type="time" value={form.in_time} onChange={(e) => saveSoon({ in_time: e.target.value })} onBlur={() => save({})} className="h-7 w-full border border-slate-200 rounded-sm px-1.5 font-mono-num text-[11.5px]" data-testid="attendance-editor-in" />
        </label>
        <label className="block">
          <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-500">Out time</div>
          <input type="time" value={form.out_time} onChange={(e) => saveSoon({ out_time: e.target.value })} onBlur={() => save({})} className="h-7 w-full border border-slate-200 rounded-sm px-1.5 font-mono-num text-[11.5px]" data-testid="attendance-editor-out" />
        </label>
      </div>
      <input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} onBlur={() => save({})} onKeyDown={(e) => e.key === "Enter" && save({})} placeholder="Remarks (optional)" className="h-7 w-full border border-slate-200 rounded-sm px-1.5 text-[11px] mb-2" data-testid="attendance-editor-remarks" />

      {day.source === "self" && <PunchDetails day={day} />}

      <div className={`rounded-sm px-2 py-1.5 text-[10.5px] ${isLate || isEarly ? "bg-orange-50 text-orange-800 border border-orange-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`} data-testid="attendance-editor-rule">
        {isLate ? <><b>Late by {lateMin} min</b> (shift {settings.shift_in}, grace {settings.late_grace_min} min) → half-day deduction{settings.late_tolerance_count ? ` unless within the first ${settings.late_tolerance_count} late marks` : ""}.</>
          : lateMin > 0 ? <>Late by {lateMin} min — within the {settings.late_grace_min}-min grace, no deduction.</>
          : <>Shift {settings.shift_in} – {settings.shift_out}. Arriving more than {settings.late_grace_min} min late cuts half a day.</>}
        {isEarly && <> <b>Early exit {earlyMin} min</b> → half-day.</>}
        {hours && <> · {hours} h worked</>}
        {saving && <span className="ml-1 text-slate-400">saving…</span>}
      </div>
    </div>
  );
}

function PunchDetails({ day }) {
  return (
    <div className="mb-2 rounded-sm border border-sky-200 bg-sky-50/60 px-2 py-1.5 text-[10.5px] text-slate-700" data-testid="attendance-editor-punch">
      <div className="flex items-center gap-1 font-semibold text-sky-800"><DeviceMobile size={12} /> Self-punched from mobile{day.edited_by_admin ? <span className="text-slate-500 font-normal">· edited by admin</span> : null}</div>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <Punch label="In" p={day.punch_in} />
        <Punch label="Out" p={day.punch_out} />
      </div>
    </div>
  );
}

function Punch({ label, p }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    if (p?.photo_path) {
      api.get("/payroll/punch/photo", { params: { path: p.photo_path }, responseType: "blob" })
        .then(({ data }) => { revoke = URL.createObjectURL(data); setUrl(revoke); }).catch(() => setUrl(null));
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [p?.photo_path]);
  if (!p) return <div className="text-slate-400">{label}: —</div>;
  return (
    <div className="flex gap-2 items-start">
      {url ? <img src={url} alt={`${label} selfie`} className="w-12 h-12 object-cover rounded-sm border border-slate-200" data-testid={`punch-photo-${label.toLowerCase()}`} /> : <div className="w-12 h-12 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center text-[8px] text-slate-400">no photo</div>}
      <div className="leading-tight">
        <div className="font-mono-num font-semibold">{label} {p.local_time}</div>
        <div className={`flex items-center gap-0.5 ${p.outside ? "text-rose-700 font-semibold" : p.outside === false ? "text-emerald-700" : "text-slate-500"}`}>
          <MapPin size={10} /> {p.outside == null ? (p.lat == null ? "no location" : "no site configured") : p.outside ? `outside · ${p.distance_m} m from ${p.site_name}` : `${p.distance_m} m · ${p.site_name}`}
        </div>
        {p.accuracy_m != null && <div className="text-slate-400">±{p.accuracy_m} m GPS</div>}
      </div>
    </div>
  );
}
